import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material"
import { blue } from "@mui/material/colors"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { alertError, alertSuccess, alertConfirmDelete } from "components/Alert"
import { DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { addIdForDataGrid } from "utils"
import { pcItmMatchColumns, notIsCmptColumns } from "./columns"
import { LinkRounded, DeleteRounded } from "resource/muiImport"
import PettyCashDialog from "../PettyCash/PettyCashDialog"

// recompute amount-before-vat / pay-amount and wht defaults when a cell is edited
const recalcRow = (row, field, value) => {
  const updated = { ...row, [field]: value }
  const vatPrct = Number(updated.VatPrct || 0)
  const whtPrct = Number(updated.WhtPrct || 0)
  let amnt = Number(updated.Amnt || 0)
  let amntB4Vat = amnt / (1 + vatPrct / 100)
  if (field === "Amnt") {
    amnt = Number(value || 0)
    amntB4Vat = amnt / (1 + vatPrct / 100)
  } else if (field === "AmntB4Vat") {
    amntB4Vat = Number(value || 0)
    amnt = amntB4Vat * (1 + vatPrct / 100)
  }
  let whtPayrTyp = updated.WhtPayrTyp
  let whtTypId = updated.WhtTypId
  if (field === "WhtPrct") {
    if (Number(value) > 0) {
      if (!whtPayrTyp) whtPayrTyp = "A"
      if (!whtTypId) whtTypId = Number(value) === 1 ? 1 : 5
    } else if (Number(value) === 0) {
      whtPayrTyp = null
      whtTypId = null
    }
  }
  const payAmnt = ((amnt / (1 + vatPrct / 100)) * (1 - whtPrct / 100))
    + (((amnt / (1 + vatPrct / 100)) * vatPrct) / 100)
  return { ...updated, Amnt: amnt, AmntB4Vat: amntB4Vat, PayAmnt: payAmnt, WhtPayrTyp: whtPayrTyp, WhtTypId: whtTypId }
}

// ids of the pc_items currently being dragged from the left table
let draggedPCItmIds = []

let selectedPCTrnsId = null
const MatchAdvanceExpenseDialog = ({ dialogOpen, setDialogOpen, selectedDrvId, onFinish, pcTrnsId: scopePCTrnsId = null }) => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [leftRows, setLeftRows] = useState([])
  const [rightRows, setRightRows] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [pcJnlId, setPcJnlId] = useState(null)
  const [dialogPettyCashOpen, setDialogPettyCashOpen] = useState(false)

  const selectionModelRef = useRef([])
  selectionModelRef.current = selectionModel

  // single call returns both the unlinked receipts (left) and the not-complete
  // pc_transactions (right) via WithNoIsCmpt. When pcTrnsId is given (opened from
  // PettyCashDialog), the right table is scoped to that single transaction.
  const getData = useCallback(() => {
    if (!selectedDrvId) return
    ax.post("/petty-cash/getPCItemFileDriverTable", {
      DrvId: selectedDrvId, OnlyUnlinked: 1, WithNoIsCmpt: 1, NoIsCmptPCTrnsId: scopePCTrnsId,
    }).then(value => {
      if (value.data) {
        setLeftRows(addIdForDataGrid(value.data.pcItems, "PCItmId"))
        setRightRows(addIdForDataGrid(value.data.noIsCmpt, "PCTrnsId"))
      }
    })
  }, [ax, selectedDrvId, scopePCTrnsId])

  useEffect(() => {
    if (dialogOpen && selectedDrvId) {
      setSelectionModel([])
      setPcJnlId(msData.pcJournalCombo?.find(item => item.IsDf)?.id || null)
      getData()
    }
  }, [dialogOpen, selectedDrvId, getData, msData.pcJournalCombo])

  const handleRowDragStart = useCallback((e) => {
    const id = Number(e.currentTarget.getAttribute("data-id"))
    const selected = selectionModelRef.current
    draggedPCItmIds = (selected.length > 0 && selected.includes(id))
      ? selected.map(Number)
      : [id]
    e.dataTransfer.effectAllowed = "move"
  }, [])

  const handleRowDragOver = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.classList.add("drop-target")
  }, [])

  const handleRowDragLeave = useCallback((e) => {
    e.currentTarget.classList.remove("drop-target")
  }, [])

  const handleRowDrop = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.classList.remove("drop-target")
    const pcTrnsId = Number(e.currentTarget.getAttribute("data-id"))
    if (!pcTrnsId || draggedPCItmIds.length === 0) return
    const draggedRows = leftRows.filter(r => draggedPCItmIds.includes(r.PCItmId))
    if (draggedRows.some(r => !r.ExpId)) {
      alertError("ใบเสร็จที่ลากมาบางรายการไม่มีประเภทค่าใช้จ่าย กรุณาระบุก่อนจับคู่")
      draggedPCItmIds = []
      return
    }
    const postData = {
      PCItmIds: draggedPCItmIds,
      PCTrnsId: pcTrnsId,
      getFn: "getPCItemFileDriverTable",
      getArgs: { DrvId: selectedDrvId, OnlyUnlinked: 1, WithNoIsCmpt: 1, NoIsCmptPCTrnsId: scopePCTrnsId },
    }
    ax.post("/petty-cash/updatePCItemPCTrns", postData).then(value => {
      if (value.data) {
        alertSuccess("ผูกใบเสร็จกับรายการเบิกเงินเรียบร้อยแล้ว")
        draggedPCItmIds = []
        setSelectionModel([])
        setLeftRows(addIdForDataGrid(value.data.pcItems, "PCItmId"))
        setRightRows(addIdForDataGrid(value.data.noIsCmpt, "PCTrnsId"))
        if (onFinish) onFinish()
      }
    })
  }, [ax, leftRows, onFinish, selectedDrvId, scopePCTrnsId])

  const leftColumns = useMemo(() => pcItmMatchColumns({
    expCombo: msData.expOprCombo,
    whtTypeCombo: msData.whtTypeCombo,
    supplierCombo: msData.supplierCombo,
  }), [msData.expOprCombo, msData.whtTypeCombo, msData.supplierCombo])

  const handleLeftEditCommit = useCallback((params) => {
    setLeftRows(o => {
      const result = [...o]
      const idx = result.findIndex(r => r.id === params.id)
      if (idx === -1) return result
      const newRow = recalcRow(result[idx], params.field, params.value)
      result[idx] = newRow
      // persist the edited pc_item columns (no calDiffAmntAndUpdate - receipt has no PayAmnt-based transaction)
      ax.post("/petty-cash/updatePCItemCols", {
        PCItmId: newRow.PCItmId,
        ColValues: {
          ExpId: newRow.ExpId ?? null,
          Amnt: newRow.Amnt ?? 0,
          VatPrct: newRow.VatPrct ?? 0,
          WhtPrct: newRow.WhtPrct ?? 0,
          WhtPayrTyp: newRow.WhtPayrTyp ?? null,
          WhtTypId: newRow.WhtTypId ?? null,
          SupId: newRow.SupId ?? null,
        },
      }, false)
      return result
    })
  }, [ax])

  const handleReimburse = useCallback(() => {
    if (selectionModel.length === 0) {
      alertError("กรุณาเลือกใบเสร็จอย่างน้อย 1 รายการ")
      return
    }
    if (!pcJnlId) {
      alertError("กรุณาเลือกสมุดเงินสดย่อย")
      return
    }
    const selectedRows = leftRows.filter(row => selectionModel.includes(row.id))
    if (selectedRows.some(row => !row.ExpId)) {
      alertError("ใบเสร็จที่เลือกบางรายการไม่มีประเภทค่าใช้จ่าย ไม่สามารถทำเบิกได้")
      return
    }
    const amnt = selectedRows.reduce((sum, row) => sum + Number(row.Amnt || 0), 0)
    const expId = selectedRows[0].ExpId
    const jobOrdId = selectedRows[0].JobOrdId
    const postData = {
      PCItmIds: selectionModel.map(Number),
      Amnt: amnt,
      ExpId: expId,
      PCJnlId: pcJnlId,
      DrvId: selectedDrvId,
      JobOrdId: jobOrdId,
    }
    ax.post("/petty-cash/reimbursePCItemFromReceipt", postData).then(value => {
      if (value.data) {
        alertSuccess("สร้างรายการเบิกเงินเรียบร้อยแล้ว")
        setSelectionModel([])
        getData()
        if (onFinish) onFinish()
      }
    })
  }, [ax, selectionModel, pcJnlId, leftRows, selectedDrvId, getData, onFinish])

  const handleDelete = useCallback(() => {
    if (selectionModel.length === 0) return
    alertConfirmDelete(`ต้องการลบใบเสร็จ ${selectionModel.length} รายการ หรือไม่`, () => {
      ax.post("/petty-cash/deleteDriverReceiptList", { PCItmIds: selectionModel.map(Number) }).then(value => {
        if (value.data) {
          setSelectionModel([])
          getData()
          if (onFinish) onFinish()
        }
      })
    })
  }, [ax, selectionModel, getData, onFinish])

  const drvNm = useMemo(() =>
    msData.driverCombo?.find(item => item.id === selectedDrvId)?.label || ""
    , [msData.driverCombo, selectedDrvId])

  const handlePCTrnsCellClick = useCallback(({ field, id }) => {
    if (field === "ShowDetail") {
      selectedPCTrnsId = id 
      setDialogPettyCashOpen(true)
    }
  }, [])

  const handlePettyCashDialogFinish = useCallback(() => {
    getData()
  }, [getData])
  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperProps={{
        style: {
          maxWidth: 2150
        }
      }}
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`จับคู่ค่าใช้จ่ายล่วงหน้า - ${drvNm}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC height="75vh" pt={1} sx={{ gap: 1 }}>
          <BoxFR sx={{ gap: 1 }}>
            <ComboBox options={msData.pcJournalCombo} label="สมุดเงินสดย่อย" sx={{ width: 200 }}
              selectedId={pcJnlId}
              setSelectedId={id => setPcJnlId(id)}
            />
            <Button variant="contained" color="primary" onClick={handleReimburse}>
              <LinkRounded sx={{ mr: 1 }} /> สร้างการเบิกจากใบเสร็จที่เลือก
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}
              disabled={selectionModel.length === 0}>
              <DeleteRounded sx={{ mr: 1 }} /> ลบ ({selectionModel.length})
            </Button>
            <Box flex={1} />
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
              ลากใบเสร็จจากตารางบน ไปวางที่รายการเบิกเงินในตารางล่าง
            </Typography>
          </BoxFR>
          <BoxFC height="100%" sx={{ gap: 1, alignItems: "stretch" }}>
            <BoxFC flex={1} sx={{ gap: 0.5 }} >
              <Typography fontWeight="bold" sx={{ pl: 1 }}>ใบเสร็จที่ยังไม่ได้ผูก</Typography>
              <Box height="100%">
                <DataGridCellExpand
                  hideFooter
                  checkboxSelection
                  disableSelectionOnClick
                  selectionModel={selectionModel}
                  onSelectionModelChange={setSelectionModel}
                  rows={leftRows}
                  columns={leftColumns}
                  onCellEditCommit={handleLeftEditCommit}
                  componentsProps={{
                    row: { draggable: true, onDragStart: handleRowDragStart },
                  }}
                />
              </Box>
            </BoxFC>
            <BoxFC flex={1} sx={{ gap: 0.5 }} >
              <Typography fontWeight="bold" sx={{ pl: 1 }}>รายการเบิกเงินที่ยังไม่ครบ</Typography>
              <Box height="100%" sx={{
                "& .MuiDataGrid-row.drop-target": {
                  bgcolor: blue[100],
                  outline: `2px dashed ${blue[400]}`,
                  outlineOffset: -2,
                },
              }}>
                <DataGridCellExpand
                  hideFooter
                  density="standard"
                  rows={rightRows}
                  columns={notIsCmptColumns}
                  onCellClick={handlePCTrnsCellClick}
                  componentsProps={{
                    row: {
                      onDragOver: handleRowDragOver,
                      onDragLeave: handleRowDragLeave,
                      onDrop: handleRowDrop,
                    },
                  }}
                />
              </Box>
            </BoxFC>
          </BoxFC>
          <PettyCashDialog
            selectedId={selectedPCTrnsId}
            dialogOpen={dialogPettyCashOpen}
            setDialogOpen={setDialogPettyCashOpen}
            onFinish={handlePettyCashDialogFinish}/>
        </BoxFC>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(MatchAdvanceExpenseDialog)
